import React from 'react'
import { useIntl } from 'react-intl'
import _ from 'lodash'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import { Box, TableHead } from '@mui/material'
import { CheckCircleRounded } from '@mui/icons-material'
import Unchecked from '../../../../../../assets/icons/UncheckedIcon.svg'
import EditIcon from '../../../../../../assets/icons/Edit/Edit.svg'
import ConfirmDelete from '../../../../../../components/dialogs/ConfirmDelete'
import { CoursePriceDetailsState } from '../../../../../../features/types'
import UnlockCourseDialog from '../../UnlockCourseDialog'

interface Props {
    prices: CoursePriceDetailsState[]
    isFetching: boolean
    handleOpen: (coursePrice: CoursePriceDetailsState) => void
    onActivate: (id: number) => void
    onDelete: (id: number) => void
}

const styles = {
    tableContainer: {
        border: 1,
        borderColor: 'grey.100',
        borderRadius: 2,
    },
    table: {
        minWidth: 500,
    },
    tableHead: {
        bgcolor: 'grey.100',
    },
    flexEnd: {
        display: 'flex',
        justifyContent: 'end',
    },
    iconButton: {
        background: '#f3f4f5',
        mr: 2,
    },
}

const PriceTable: React.FC<Props> = ({
    prices = [],
    isFetching = false,
    handleOpen,
    onActivate,
    onDelete,
}) => {
    const { formatMessage, locale } = useIntl()

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={styles.tableContainer}
                elevation={0}
            >
                <Table
                    sx={styles.table}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={styles.tableHead}>
                        <TableRow>
                            <TableCell>#</TableCell>

                            <TableCell>
                                {formatMessage({ id: 'nameSubject' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'subjects' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'promoCompaign' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'packageDiscount' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'status' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'optimalPrice' })}
                            </TableCell>

                            <TableCell
                                align="right"
                                width={12}
                            >
                                {formatMessage({ id: 'actions' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {prices.length
                            ? prices.map((row, index) => (
                                  <TableRow key={row.id}>
                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {index + 1}
                                          .
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {row[`title${_.capitalize(locale)}`]}
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {row.countSubjects ? (
                                              <CheckCircleRounded
                                                  color="primary"
                                                  sx={{ mr: 1 }}
                                              />
                                          ) : (
                                              <img src={Unchecked} />
                                          )}
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {row.countPromotions ? (
                                              <CheckCircleRounded
                                                  color="primary"
                                                  sx={{ mr: 1 }}
                                              />
                                          ) : (
                                              <img src={Unchecked} />
                                          )}
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {row.countDiscountPackages ? (
                                              <CheckCircleRounded
                                                  color="primary"
                                                  sx={{ mr: 1 }}
                                              />
                                          ) : (
                                              <img src={Unchecked} />
                                          )}
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          <UnlockCourseDialog
                                              title={
                                                  row[
                                                      `title${_.capitalize(
                                                          locale,
                                                      )}`
                                                  ]
                                              }
                                              isActive={row.isActive}
                                              confirmAction={() =>
                                                  onActivate(row.id as number)
                                              }
                                          />
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {row.optimalPrice}
                                      </TableCell>

                                      <TableCell align="right">
                                          <Box style={styles.flexEnd}>
                                              <IconButton
                                                  sx={styles.iconButton}
                                                  onClick={() =>
                                                      handleOpen(row)
                                                  }
                                              >
                                                  <img
                                                      src={EditIcon}
                                                      alt="edit"
                                                  />
                                              </IconButton>

                                              <ConfirmDelete
                                                  confirmAction={() =>
                                                      onDelete(row.id)
                                                  }
                                              />
                                          </Box>
                                      </TableCell>
                                  </TableRow>
                              ))
                            : !isFetching && (
                                  <TableRow>
                                      <TableCell
                                          component="th"
                                          scope="row"
                                          align="center"
                                          colSpan={8}
                                      >
                                          {formatMessage({ id: 'noData' })}
                                      </TableCell>
                                  </TableRow>
                              )}

                    </TableBody>

                </Table>
            </TableContainer>
        </Box>
    )
}

export default PriceTable
